import { isPostflopPositionAvailable } from '../../utils/postflopAvailability';

export const resultsPositionTypeOptions = [
  { label: 'SRP', value: 'SRP' },
  { label: '3bet Pot', value: '3bP' },
  { label: '4bet Pot', value: '4bP' },
];

export const headsUpResultsPositionTypeOptions = resultsPositionTypeOptions;

export const resultsPfrTypeOptions = [
  { label: 'PFR IP', value: 'pfr_ip' },
  { label: 'PFR OOP', value: 'pfr_oop' },
];

export const resultsPlayerPositionOptions = [
  { label: 'IP', value: 'ip' },
  { label: 'OOP', value: 'oop' },
];

const previousActionLabels = {
  initial: 'Check or C-bet',
  check: 'Vs Check',
  donk: 'Vs Donk',
  checkraise: 'Vs Check Raise',
  raise: 'Vs Raise',
  cbet: 'Vs C-bet',
  bet: 'Vs Bet',
};

const getBasePositionValue = (value = '') => value.split('_')[0];
const getPreviousActionValue = (value = '') => value.split('_')[1] || 'initial';

// Challenge IDs describe the hero first; database table names instead describe
// the preflop matchup. Keep the saved IDs intact and derive labels from them.
const getBasePositionLabel = (value = '') => getBasePositionValue(value)
  .replace(/BTN/g, 'BU')
  .replace(/UTG/g, 'EP')
  .replace('vs', ' vs ');

const createOptions = (positions, actions, initialLabel = 'Check or C-bet') => (
  positions.reduce((options, position) => options.concat(actions.map((action) => ({
    label: `${getBasePositionLabel(position)}${action ? ` ${action === 'checkraise' ? 'check raise' : action}` : ''}`,
    value: `${position}${action ? `_${action}` : ''}`,
    actionLabel: action ? previousActionLabels[action] : initialLabel,
  }))), [])
);

const createPfrOptions = (ipPositions, oopPositions, pfrIsIp) => ({
  ip: createOptions(ipPositions, ['check', pfrIsIp ? 'donk' : 'cbet', 'checkraise']),
  oop: createOptions(
    oopPositions,
    ['', 'raise', pfrIsIp ? 'cbet' : 'bet'],
    pfrIsIp ? 'Check or Donk' : 'Check or C-bet',
  ),
});

// These category IDs match GetSpecificPositionAndPreviousActionsByPosition in
// the API. In particular, callers must not reverse the ID to fix a label.
export const resultsPositionOptions = {
  SRP: {
    pfr_ip: createPfrOptions(['EPvsBB', 'BUvsSB', 'BUvsBB'], ['BBvsEP', 'SBvsBU', 'BBvsBU'], true),
    pfr_oop: createPfrOptions(['BUvsEP', 'BUvsCO', 'BBvsSB'], ['EPvsBU', 'COvsBU', 'SBvsBB'], false),
  },
  '3bP': {
    pfr_ip: createPfrOptions(['BUvsEP', 'BUvsCO', 'BBvsSB'], ['EPvsBU', 'COvsBU', 'SBvsBB'], true),
    pfr_oop: createPfrOptions(['EPvsSB', 'BUvsSB'], ['SBvsEP', 'SBvsBU'], false),
  },
  '4bP': {
    pfr_ip: createPfrOptions(['EPvsSB', 'BUvsSB'], ['SBvsEP', 'SBvsBU'], true),
    pfr_oop: createPfrOptions(['BUvsEP', 'BUvsCO', 'BBvsSB'], ['EPvsBU', 'COvsBU', 'SBvsBB'], false),
  },
};

const createHeadsUpOptions = (pfrIsIp) => ({
  ip: createOptions(['SBvsBB'], ['check', pfrIsIp ? 'donk' : 'cbet', 'checkraise']),
  oop: createOptions(['BBvsSB'], ['', 'raise', pfrIsIp ? 'cbet' : 'bet'], pfrIsIp ? 'Check or Donk' : 'Check or C-bet'),
});

// The button/SB is always IP heads-up. The BB is the last preflop aggressor
// in a 3-bet pot; the button is the aggressor in SRP and 4-bet pots.
export const headsUpResultsPositionOptions = {
  SRP: { pfr_ip: createHeadsUpOptions(true) },
  '3bP': { pfr_oop: createHeadsUpOptions(false) },
  '4bP': { pfr_ip: createHeadsUpOptions(true) },
};

export const getResultsPositionOptions = (
  positionType = 'SRP',
  pfrType = 'pfr_ip',
  playerPosition = 'ip',
) => resultsPositionOptions[positionType]?.[pfrType]?.[playerPosition] || [];

export const getHeadsUpResultsPositionOptions = (
  positionType = 'SRP',
  pfrType = 'pfr_ip',
  playerPosition = 'ip',
) => headsUpResultsPositionOptions[positionType]?.[pfrType]?.[playerPosition] || [];

export const getResultsPositionTypeOptions = (stackSize, tableSize, isPlo5) => (
  resultsPositionTypeOptions.filter((option) => {
    if (stackSize === '50bb' && option.value === '4bP') return false;
    if (isPlo5 && stackSize === '50bb' && option.value === '3bP') return false;
    if (['HU', '6max'].includes(tableSize) && stackSize === '200bb' && option.value === 'SRP') return false;
    return true;
  })
);

export const isResultsPositionAvailable = (isPlo5, tableSize, stackSize, pot, spot, boardType = '', handType = '') => {
  if (!isPostflopPositionAvailable(isPlo5, tableSize, stackSize, pot, spot)) return false;
  // HU SRP at 100bb is a check-first tree. Its missing donk branches must not
  // be substituted with the six-max BTN-vs-BB solution.
  if (tableSize === 'HU' && pot === 'SRP' && (stackSize === '100bb'
    || (stackSize === '50bb' && (boardType === 'paired' || handType === 'flush')))) {
    return !['donk', 'raise'].includes(getPreviousActionValue(spot));
  }
  return true;
};

export const getResultsStepFromPosition = (value = '', pfrType = 'pfr_ip') => {
  const action = getPreviousActionValue(value);
  if (action === 'initial') return ['starting'];
  if (action === 'check') return ['check'];
  if (action === 'checkraise') return ['check', 'bet', 'raise'];
  if (action === 'raise') return ['bet', 'raise'];
  if (action === 'cbet') return pfrType === 'pfr_ip' ? ['check', 'bet'] : ['bet'];
  if (action === 'bet') return ['check', 'bet'];
  if (action === 'donk') return ['bet'];
  return [];
};

export const getResultsBasePositionOptions = (
  positionType = 'SRP',
  pfrType = 'pfr_ip',
  playerPosition = 'ip',
  positionOptions = resultsPositionOptions,
) => {
  const options = positionOptions[positionType]?.[pfrType]?.[playerPosition] || [];
  const result = [];

  options.forEach((option) => {
    const baseValue = getBasePositionValue(option.value);
    if (result.find((item) => item.value === baseValue)) return;

    result.push({ label: getBasePositionLabel(baseValue), value: baseValue });
  });

  return result;
};

export const getResultsPreviousActionOptions = (
  positionType = 'SRP',
  pfrType = 'pfr_ip',
  playerPosition = 'ip',
  basePosition = '',
  positionOptions = resultsPositionOptions,
) => (positionOptions[positionType]?.[pfrType]?.[playerPosition] || [])
  .filter((option) => getBasePositionValue(option.value) === basePosition)
  .map((option) => {
    const action = getPreviousActionValue(option.value);

    return {
      label: option.actionLabel || previousActionLabels[action],
      value: option.value,
      action,
    };
  });

export const getDefaultResultsPosition = (
  positionType = 'SRP',
  pfrType = 'pfr_ip',
  playerPosition = 'ip',
) => getResultsPositionOptions(positionType, pfrType, playerPosition)[0] || null;

export const getDefaultResultsPreviousAction = (
  positionType = 'SRP',
  pfrType = 'pfr_ip',
  playerPosition = 'ip',
  basePosition = '',
  positionOptions = resultsPositionOptions,
) => getResultsPreviousActionOptions(
  positionType,
  pfrType,
  playerPosition,
  basePosition,
  positionOptions,
)[0] || null;

const prioritizeOption = (options = [], preferred = null) => [
  ...options.filter((option) => option.value === preferred?.value),
  ...options.filter((option) => option.value !== preferred?.value),
];

export const getCompatibleResultsSelection = (
  positionType,
  pfrType,
  playerPosition,
  isPositionAvailable = () => true,
  positionTypeOptions = resultsPositionTypeOptions,
  positionOptions = resultsPositionOptions,
) => {
  const selectedPositionType = positionTypeOptions.find(
    (option) => option.value === positionType?.value,
  ) || positionTypeOptions[0];
  if (!selectedPositionType) return null;

  const selectedPositions = positionOptions[selectedPositionType.value] || {};
  const pfrOptions = prioritizeOption(resultsPfrTypeOptions, pfrType);
  const playerOptions = prioritizeOption(resultsPlayerPositionOptions, playerPosition);
  const combinations = pfrOptions.reduce((result, currentPfrType) => (
    result.concat(playerOptions.map((currentPlayerPosition) => ({
      pfrType: currentPfrType,
      playerPosition: currentPlayerPosition,
      positions: (
        selectedPositions[currentPfrType.value]?.[currentPlayerPosition.value]
        || []
      ).filter((option) => isPositionAvailable(option, selectedPositionType.value)),
    })))
  ), []);
  const selection = combinations.find((combination) => combination.positions.length > 0);

  return selection ? {
    positionType: selectedPositionType,
    pfrType: selection.pfrType,
    playerPosition: selection.playerPosition,
    position: selection.positions[0],
  } : null;
};
